import './taskManagerStyles.css';

import { useRef } from 'react';
import {
  FilterCriterionConfig,
  FilterCriteriaState,
  BaseNode,
  TreeUI,
  Action,
  findNode,
} from 'nested-core';
import { Task, isTask } from '../models/task/Task.ts';
import Domain, { isDomain } from '../models/domain/Domain.ts';
import { downloadExportData } from '../data/downloadExportData';
import nodeSort from '../nodeSort.ts';
import { renderTitle } from '../components/NodeTitle/NodeTitle.tsx';
import renderEntryForm from '../renderEntryForm.tsx';

import { useTaskManagerState } from '../useTaskManagerState.ts';

import { parseData } from '../data/parseData.ts';

function TaskManagerPage() {
  const {
    dataTree,
    switchDone,
    addChildNode,
    deleteNode,
    toggleArchiveDomain,
    replaceTree,
    updateNode,
  } = useTaskManagerState();

  const initialFilterCriteria: FilterCriterionConfig[] = [
    {
      name: 'showCompleteTasks',
      label: 'Show complete tasks',
      initialValue: false,
    },
    {
      name: 'showArchivedDomains',
      label: 'Show archived domains',
      initialValue: false,
    },
  ];

  function renderCustomDataFields(node: BaseNode) {
    return (
      <>
        {'done' in node && (
          <span className="actions">
            <label>
              <input
                type="checkbox"
                name="done"
                checked={(node as Task).done}
                onChange={() => {
                  switchDone({ nodeId: node.id });
                }}
              />
              Done?
            </label>
          </span>
        )}
        {'dueDate' in node && node.dueDate && (
          <p>
            <u>Duedate:</u>
            {' '}
            {node.dueDate.toString()}
          </p>
        )}
        {'instructions' in node && node.instructions && (
          <p>
            <u>Instructions:</u>
            {' '}
            {node.instructions as string}
          </p>
        )}
      </>
    );
  }

  function isFilteredOut(
    node: BaseNode,
    filterCriteria: FilterCriteriaState,
  ): boolean {
    switch (node.type) {
      case 'task':
        if (
          (node as Task).done === true
          && filterCriteria.showCompleteTasks.value === false
        ) {
          return true;
        }
        return false;
      case 'domain':
        if (
          (node as Domain).archived === true
          && filterCriteria.showArchivedDomains.value === false
        ) {
          return true;
        }
        return false;
      default:
        return false;
    }
  }

  const actions: {
    [key: string]: Action;
  } = {
    createNewChildTask: {
      type: 'node',

      label: '+ Create New Task',

      renderer: ({
        hidePopup,
        callerId,
      }: {
        hidePopup?: () => void;
        callerId: string;
      }) => renderEntryForm({
        nodeType: 'task',
        formType: 'create',
        onMenuClose: hidePopup,
        parentId: callerId,
        addChildNode,
      }),
    },

    createNewChildDomain: {
      type: 'node',

      label: '+ Create New Domain',

      renderer: ({
        hidePopup,
        callerId,
      }: {
        hidePopup?: () => void;
        callerId: string;
      }) => renderEntryForm({
        nodeType: 'domain',
        formType: 'create',
        onMenuClose: hidePopup,
        parentId: callerId,
        addChildNode,
      }),
    },

    toggleArchiveDomain: {
      type: 'node',

      label: 'Archive',

      execute: ({ callerId }: { callerId: string }) => toggleArchiveDomain({ nodeId: callerId }),
    },

    toggleUnarchiveDomain: {
      type: 'node',

      label: 'Unarchive',

      execute: ({ callerId }: { callerId: string }) => toggleArchiveDomain({ nodeId: callerId }),
    },

    deleteTask: {
      type: 'node',

      label: 'Delete',

      execute: ({ callerId }: { callerId: string }) => deleteNode({ nodeId: callerId }),
    },

    downloadExportData: {
      type: 'global',

      label: 'Export Data',

      execute: () => downloadExportData(),
    },

    uploadImportData: {
      type: 'global',

      label: 'Import Data',

      ownRenderer: () => {
        const fileInputRef = useRef<HTMLInputElement | null>(null);

        function openFilePicker() {
          fileInputRef.current?.click();
        }

        async function handleFileChange(
          e: React.ChangeEvent<HTMLInputElement>,
        ) {
          const file = e.target.files?.[0];
          if (!file) return;

          const fileText = await file.text();

          let newTree: BaseNode;
          try {
            newTree = parseData(fileText);
          } catch (error) {
            console.log(
              `Failed to parse imported data: ${(error as Error).message}`,
            );
            return;
          }

          replaceTree({ newTree });
        }

        return (
          <>
            <button type="button" onClick={openFilePicker}>
              Import Data
            </button>

            <input
              type="file"
              accept=".json"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </>
        );
      },
    },

    editDomain: {
      type: 'node',

      label: 'Edit',

      renderer: ({
        hidePopup,
        callerId,
      }: {
        hidePopup?: () => void;
        callerId: string;
      }) => {
        const currentNode = findNode({ root: dataTree, nodeId: callerId });
        if (!currentNode || !isDomain(currentNode)) {
          return null;
        }

        return renderEntryForm({
          nodeType: 'domain',
          formType: 'edit',
          targetNodeId: callerId,
          onMenuClose: hidePopup,
          updateNode,
          initialState: {
            title: currentNode.title,
          },
        });
      },
    },

    editTask: {
      type: 'node',

      label: 'Edit',

      renderer: ({
        hidePopup,
        callerId,
      }: {
        hidePopup?: () => void;
        callerId: string;
      }) => {
        const currentNode = findNode({ root: dataTree, nodeId: callerId });
        if (!currentNode || !isTask(currentNode)) {
          return null;
        }

        return renderEntryForm({
          nodeType: 'task',
          formType: 'edit',
          targetNodeId: callerId,
          onMenuClose: hidePopup,
          updateNode,
          initialState: {
            title: currentNode.title ?? undefined,
            dueDate: currentNode.dueDate ?? undefined,
            instructions: currentNode.instructions ?? undefined,
          },
        });
      },
    },
  };

  const headerActions: string[] = ['downloadExportData', 'uploadImportData'];

  const getNodeActionsList = (node: BaseNode) => {
    switch (node.type) {
      case 'task': {
        return ['createNewChildTask', 'editTask', 'deleteTask'];
      }
      case 'domain': {
        const domainActionsList = [
          'createNewChildTask',
          'createNewChildDomain',
          'editDomain',
        ];

        if (node instanceof Domain && node.archived) {
          domainActionsList.push('toggleUnarchiveDomain');
        } else {
          domainActionsList.push('toggleArchiveDomain');
        }
        return domainActionsList;
      }
      default:
        return [];
    }
  };

  const treeUI = (
    <TreeUI
      initialFilterCriteria={initialFilterCriteria}
      nodeSort={nodeSort}
      renderTitle={renderTitle}
      dataTree={dataTree}
      renderCustomDataFields={renderCustomDataFields}
      isFilteredOut={isFilteredOut}
      actions={actions}
      headerActions={headerActions}
      getNodeActionsList={getNodeActionsList}
    />
  );
  return treeUI;
}

export default TaskManagerPage;
